import { supabase } from '../../lib/supabase';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  getUserDataFromToken,
  saveUserData,
  type UserData,
} from './tokenService';

export interface SessionResult {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  userData?: UserData;
  error?: string;
}

export async function setSession(
  accessToken: string,
  refreshToken?: string
): Promise<SessionResult> {
  try {
    const { error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken || accessToken,
    });

    if (error) {
      console.warn('Failed to set Supabase session:', error.message);
      return { success: false, error: error.message };
    }

    setTokens(accessToken, refreshToken);

    const userData = getUserDataFromToken(accessToken);
    if (userData) {
      saveUserData(userData);
    }

    return { success: true, accessToken, refreshToken, userData: userData || undefined };
  } catch (error) {
    console.error('Error setting session:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to set session',
    };
  }
}

export async function refreshSession(): Promise<SessionResult> {
  try {
    const { data, error } = await supabase.auth.refreshSession();

    if (error || !data.session) {
      console.warn('Failed to refresh session:', error?.message);
      return { success: false, error: error?.message || 'No session returned' };
    }

    const { access_token, refresh_token } = data.session;
    setTokens(access_token, refresh_token);

    const userData = getUserDataFromToken(access_token);
    if (userData) {
      saveUserData(userData);
    }

    return { success: true, accessToken: access_token, refreshToken: refresh_token, userData: userData || undefined };
  } catch (error) {
    console.error('Error refreshing session:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to refresh session',
    };
  }
}

export async function signOut(): Promise<void> {
  try {
    await supabase.auth.signOut();
  } catch (error) {
    console.warn('Error signing out from Supabase:', error);
  }
  clearTokens();
}

export async function restoreSession(): Promise<SessionResult> {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  if (!accessToken) {
    return { success: false, error: 'No access token found' };
  }

  if (refreshToken) {
    return setSession(accessToken, refreshToken);
  }

  const userData = getUserDataFromToken(accessToken);
  if (userData) {
    saveUserData(userData);
    return { success: true, accessToken, userData };
  }

  return { success: false, error: 'Failed to restore session' };
}

export async function reAuthenticateWithPassword(
  email: string,
  password: string
): Promise<SessionResult> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      return { success: false, error: error.message };
    }

    if (!data.session) {
      return { success: false, error: 'No session returned' };
    }

    const { access_token, refresh_token } = data.session;
    setTokens(access_token, refresh_token);

    const userData = getUserDataFromToken(access_token);
    if (userData) {
      saveUserData(userData);
    }

    return { success: true, accessToken: access_token, refreshToken: refresh_token, userData: userData || undefined };
  } catch (error) {
    console.error('Error re-authenticating:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to re-authenticate',
    };
  }
}
